import { ServiceResponse } from '@nx-smartmonkey/shared/hooks';
import { useQuery } from '@tanstack/react-query';
import { useEffect } from 'react';

import { getRouteMutationKey } from '.';
import { capacitorService } from '../../../../application/CapacitorService';
import { routeService } from '../../../../application/RouteService';
import { Route } from '../../../../domain/route/Route';
import { Stop } from '../../../../domain/stop/Stop';
import { StopReportCreateMapper } from '../../../../mappers/report/StopReportCreateMapper';
import { usePendingReports } from '../../hooks/pendingReports/usePendingReports';
import { useNotification } from '../../hooks/useNotification';
import { RouteActionType } from '../context/actions';
import { useRouteContext } from '../context/context';

interface UseRetrieveRouteProps {
  routeId: string;
}

type UseRetrieveRouteResponseProps = ServiceResponse<Function, Route>;

// Hooks act as 'Adapter' layer.
export const useRetrieveRoute = ({ routeId }: UseRetrieveRouteProps): UseRetrieveRouteResponseProps => {
  const { notification } = useNotification();
  const { routeState, routeDispatch } = useRouteContext();
  const { data: pendingReports } = usePendingReports();

  const {
    isInitialLoading: isLoading,
    isSuccess,
    isError,
    refetch,
  } = useQuery(
    getRouteMutationKey({ routeId }),
    () => {
      return routeService.fetchRoute({ routeId });
    },
    {
      retry: false,
      enabled: true,
      refetchOnWindowFocus: capacitorService.isNative(),
      onSuccess: async (route) => {
        if (pendingReports && pendingReports.length > 0) {
          const reportsStopsIds = pendingReports.map((report) => report.data.stopId);
          route.stops = route.stops.map((s) => {
            if (reportsStopsIds.includes(s.id)) {
              const stopReport = pendingReports.find((report) => report.data.stopId === s.id);
              if (!stopReport || !route.vehicle_id) return s;

              const report = StopReportCreateMapper.toDummyReport(stopReport, `DummyReport-${route.vehicle_id}`);
              return Stop.create(
                {
                  ...s.props,
                  stop_fields: structuredClone(s.stop_fields),
                  status: stopReport.data.status,
                  report: report,
                },
                s.id
              );
            }
            return s;
          });
        }
        routeDispatch({
          type: RouteActionType.FETCH_ROUTE_SUCCESS,
          payload: route,
        });
      },
    }
  );

  useEffect(() => {
    if (notification?.id === `smartmonkey.planner.2.planner.stop.updated`) {
      const route = notification.data;

      if (route.id !== routeId) return;

      routeDispatch({
        type: RouteActionType.FETCH_ROUTE_SUCCESS,
        payload: route,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [notification]);

  return {
    error: isError,
    success: isSuccess,
    isLoading: isLoading,
    data: routeState.route,
    run: refetch,
  };
};
